import React, {MouseEvent, useRef} from 'react'

import {Colors, IconButton, Popover} from '../index.js'
import {FieldProps} from './types.js'
import ControlLabel from './ControlLabel.js'
import type {PopoverRef} from '../overlays/Popover.js'

// The click event of the chosen color button, with target.value set to the color
export interface ColorPickerChangeEvent {
    target: {value: string}
}

export interface ColorPickerProps extends FieldProps {
    value: string
    colors?: string[]
    onChange?: (event: ColorPickerChangeEvent) => void
}

const ColorPicker = ({value, colors = Colors.routeColors, label, info, disabled, readOnly, style, onChange}: ColorPickerProps) => {
    const popover = useRef<PopoverRef>(null)

    const onClickColor = (color: string, event: MouseEvent) => {
        if (disabled || readOnly) {
            return
        }

        const changeEvent = event as unknown as ColorPickerChangeEvent
        changeEvent.target.value = color
        onChange?.(changeEvent)
        popover.current?.close()
    }

    const picker = (
        <Popover
            noClose={true}
            ref={popover}
            disabled={disabled || readOnly}
            content={
                <div style={{display: 'flex', flexWrap: 'wrap', width: 216}}>
                    {colors.map((color) => {
                        return (
                            <IconButton
                                key={color}
                                aria-label={color}
                                onClick={(event) => onClickColor(color, event)}
                            >
                                <i style={{color, fontSize: 20}} className='mdi mdi-circle' />
                            </IconButton>
                        )
                    })}
                </div>
            }
        >
            {/* Disabled uses IconButton's grey instead of the color, readOnly shows the color without hover/pointer */}
            <IconButton
                aria-label='Kleur kiezen'
                disabled={disabled}
                noHover={readOnly}
                style={{...(!disabled && {color: value}), ...(readOnly && {cursor: 'default'})}}
            >
                <i className='mdi mdi-circle' title='Kleur kiezen' />
            </IconButton>
        </Popover>
    )

    // Without label, info or style the picker is rendered bare, as callers lay it out inline
    if (!label && !info && !style) {
        return picker
    }

    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 6, ...style}}>
            {picker}

            {(label || info) &&
                <ControlLabel info={info} disabled={disabled}>
                    {label}
                </ControlLabel>
            }
        </div>
    )
}

export default ColorPicker
